'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { NormalDistributionTooltip } from './NormalDistributionTooltip';
import type { ScoreStatistics } from '@/lib/api';

// Re-export ScoreStatistics for consumers
export type { ScoreStatistics } from '@/lib/api';

interface ScoreComparisonProps {
    userScore: number | null;
    stats: ScoreStatistics | null;
    compact?: boolean; // For smaller displays
}

export function ScoreComparison({ userScore, stats, compact = false }: ScoreComparisonProps) {
    const t = useTranslations('certification.comparison');

    const comparison = useMemo(() => {
        if (!stats || stats.count === 0) {
            return null;
        }

        const mean = stats.mean;

        if (userScore === null) {
            // No user score yet, just show average
            return {
                mean,
                percentDiff: null,
                isAbove: null,
            };
        }

        const diff = userScore - mean;
        const percentDiff = mean !== 0 ? Math.round((diff / mean) * 100) : 0;

        return {
            mean,
            percentDiff,
            isAbove: diff > 0 ? true : diff < 0 ? false : null,
        };
    }, [userScore, stats]);

    if (!comparison) {
        return null;
    }

    const { mean, percentDiff, isAbove } = comparison;

    // Determine color based on comparison
    const getTextColor = () => {
        if (isAbove === null) return 'text-slate-500';
        if (isAbove) return 'text-emerald-600';
        return 'text-rose-600';
    };

    const getPercentLabel = () => {
        if (percentDiff === null) return '';
        if (percentDiff === 0) return t('equal');
        const sign = percentDiff > 0 ? '+' : '';
        return `${sign}${percentDiff}%`;
    };

    return (
        <div className="group relative inline-flex items-center">
            <div
                className={`flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-slate-100 border border-slate-200 cursor-help transition-colors hover:bg-slate-50 ${compact ? 'text-xs' : 'text-xs'}`}
            >
                <span className="text-slate-500">
                    {t('avgLabel')}: {mean.toFixed(1)}
                </span>
                {percentDiff !== null && (
                    <>
                        <span className="text-slate-300">|</span>
                        <span className={`font-medium ${getTextColor()}`}>
                            {getPercentLabel()}
                        </span>
                    </>
                )}
            </div>

            {/* Tooltip on hover */}
            {stats && stats.count > 0 && (
                <NormalDistributionTooltip
                    stats={stats}
                    userScore={userScore}
                />
            )}
        </div>
    );
}
